import React from 'react'
import { CheckinFeature, Feature } from '../api/client'
import ReferenceValueDisplay from './ReferenceValueDisplay'
import { resolveCollectionItemName } from '../utils/collectionUtils'
import { boolLabelFor } from '../utils/boolFeature'
import { useIsMobile } from '../hooks/useIsMobile'

interface MapValueDisplayProps {
  feature: Feature
  rows: CheckinFeature[]
  collectionItems: Record<string, any>
  maxPairs?: number
}

interface SubConfig {
  type?: string
  params?: any
}

// Per-segment chip cap. Pairs with `whitespace-nowrap` on the parent chip.
// Mobile is much tighter — phone width can't accommodate desktop-sized chips.
const SEG_LEN_DESKTOP = 20
const SEG_LEN_MOBILE = 12
const makeTruncate = (max: number) => (s: string): string =>
  s.length > max ? `${s.slice(0, max - 1)}…` : s

const renderKey = (
  row: CheckinFeature,
  sub: SubConfig,
  collectionItems: Record<string, any>,
  segLen: number,
): React.ReactNode => {
  const truncate = makeTruncate(segLen)
  const t = sub?.type
  if (t === 'reference') {
    if (row.key_reference_value == null) return '?'
    return (
      <ReferenceValueDisplay
        itemId={row.key_reference_value}
        showPrefix={false}
        collectionItems={collectionItems}
        maxLen={segLen}
      />
    )
  }
  if (t === 'num') {
    return row.key_decimal_value != null ? String(row.key_decimal_value) : '?'
  }
  if (t === 'bool') {
    const label = boolLabelFor(row.key_decimal_value, sub?.params)
    return label != null ? truncate(label) : '?'
  }
  return row.key_freeform_value != null ? truncate(row.key_freeform_value) : '?'
}

const renderValue = (
  row: CheckinFeature,
  sub: SubConfig,
  feature: Feature,
  collectionItems: Record<string, any>,
  segLen: number,
): React.ReactNode => {
  const truncate = makeTruncate(segLen)
  const t = sub?.type
  const params = sub?.params ?? {}
  if (t === 'reference') {
    if (row.reference_value == null) return '?'
    return (
      <ReferenceValueDisplay
        itemId={row.reference_value}
        showPrefix={false}
        collectionItems={collectionItems}
        maxLen={segLen}
      />
    )
  }
  if (t === 'num') {
    if (row.decimal_value == null) return '?'
    const unit = feature.unit || ''
    return `${row.decimal_value}${unit}`
  }
  if (t === 'bool') {
    const label = boolLabelFor(row.decimal_value, params)
    return label != null ? truncate(label) : '?'
  }
  if (t === 'rating') {
    if (row.decimal_value == null) return '?'
    const max = params.max || 5
    return `${row.decimal_value}/${max}`
  }
  if (t === 'labeled_rating') {
    if (row.decimal_value == null) return '?'
    const labels: string[] = params.labels || []
    const label = labels[Math.floor(row.decimal_value) - 1]
    if (label) return truncate(label)
    const max = params.max || 5
    return `${row.decimal_value}/${max}`
  }
  if (t === 'location') {
    if (row.reference_value != null) {
      const name = resolveCollectionItemName(row.reference_value, collectionItems)
      return name ? `📍 ${truncate(name)}` : `📍 #${row.reference_value}`
    }
    if (row.latitude != null && row.longitude != null) {
      return `📍 ${row.latitude.toFixed(2)}°, ${row.longitude.toFixed(2)}°`
    }
    return '?'
  }
  return row.freeform_value != null ? truncate(row.freeform_value) : '?'
}

const MapValueDisplay: React.FC<MapValueDisplayProps> = ({
  feature,
  rows,
  collectionItems,
  maxPairs = 2,
}) => {
  const isMobile = useIsMobile()
  const segLen = isMobile ? SEG_LEN_MOBILE : SEG_LEN_DESKTOP
  const keySub: SubConfig = feature.config?.params?.key ?? {}
  const valueSub: SubConfig = feature.config?.params?.value ?? {}
  if (rows.length === 0) return null

  const total = rows.length
  const displayCount = Math.min(maxPairs, total)
  const remaining = total - displayCount

  return (
    <span>
      :{' '}
      {rows.slice(0, displayCount).map((row, idx) => (
        <span key={idx}>
          {renderKey(row, keySub, collectionItems, segLen)}
          {' - '}
          {renderValue(row, valueSub, feature, collectionItems, segLen)}
          {idx < displayCount - 1 && ', '}
        </span>
      ))}
      {remaining > 0 && (
        <span>{isMobile ? ', more' : `, and ${remaining} more`}</span>
      )}
    </span>
  )
}

export default MapValueDisplay
